import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from '@reduxjs/toolkit';
import { RootState } from '@/store/store';
import { setGroups } from '@/store/reducers';
import { apiUser } from '@/utils/api/apiUser';
import { firebaseUtils, logger } from '@/utils';
import { isUserAnonymous } from '@/utils/helpers/user';
import { isVerifiedExtensionEvent } from '@/utils/helpers/extension-events';
import { EXTENSION_UPDATE_CONFIG_EVENT } from '@/constants/external/extension';
import { LOGGER_FEATURE, LOGGER_ACTION } from '@/constants/logger/logger-tags';

const GROUPS_CHANGE_KEY = 'groups';
const VISIBILITY_CHANGE_EVENT = 'visibilitychange';

interface IGroupsRefresher {
  readonly refresh: () => Promise<void>;
  readonly stop: () => void;
}

// only the latest request may update the store, and nothing lands after stop,
// so a slow response cannot win or reach a user who already logged in or out
const createGroupsRefresher = (dispatch: Dispatch): IGroupsRefresher => {
  let latestRequestId = 0;
  let isActive = true;
  const refresh = async (): Promise<void> => {
    const requestId = ++latestRequestId;
    try {
      const userConfig = await apiUser.getUserConfig();
      const isLatest = isActive && requestId === latestRequestId;
      if (!isLatest || !userConfig?.groups) return;
      dispatch(setGroups(userConfig.groups));
    } catch (error) {
      logger.error('Failed to refresh groups from server', error, {
        tags: {
          feature: LOGGER_FEATURE.GROUPS,
          action: LOGGER_ACTION.SYNC_FROM_REMOTE,
        },
      });
    }
  };
  const stop = (): void => {
    isActive = false;
  };
  return { refresh, stop };
};

// hidden tabs wait until they are shown, so open tabs do not all fetch on every change
const createVisibleTabTrigger = (refresh: () => void) => {
  let isPending = false;
  const onVisibilityChange = () => {
    if (document.hidden || !isPending) return;
    isPending = false;
    refresh();
  };
  document.addEventListener(VISIBILITY_CHANGE_EVENT, onVisibilityChange);

  const trigger = () => {
    if (document.hidden) {
      isPending = true;
      return;
    }
    refresh();
  };
  const stop = () =>
    document.removeEventListener(VISIBILITY_CHANGE_EVENT, onVisibilityChange);
  return { trigger, stop };
};

const hasGroupsChange = (event: CustomEventInit): boolean => {
  const changedKeys = (event.detail?.changedKeys ?? []) as string[];
  return changedKeys.includes(GROUPS_CHANGE_KEY);
};

const listenToExtensionGroupsChanges = (onChange: () => void): (() => void) => {
  const handler = (event: CustomEventInit) => {
    if (!isVerifiedExtensionEvent(event) || !hasGroupsChange(event)) return;
    onChange();
  };

  document.addEventListener(EXTENSION_UPDATE_CONFIG_EVENT, handler);
  return () =>
    document.removeEventListener(EXTENSION_UPDATE_CONFIG_EVENT, handler);
};

/**
 * Keeps groups in sync with changes made outside this tab. Logged-in users follow the
 * Firestore change flag, which already covers extension writes, so they ignore the
 * extension ping. Anonymous users have no Firestore flag and rely on the ping.
 */
export const useGroupsSync = (appReady: boolean): void => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user);
  const uid = user?.uid;
  const isLoggedIn = !isUserAnonymous(user);

  useEffect(() => {
    if (!appReady || !uid) return;
    const refresher = createGroupsRefresher(dispatch);
    const visibleTab = createVisibleTabTrigger(refresher.refresh);
    const unsubscribe = isLoggedIn
      ? firebaseUtils.subscribeToUserConfigChanges(uid, visibleTab.trigger)
      : listenToExtensionGroupsChanges(visibleTab.trigger);
    return () => {
      unsubscribe();
      visibleTab.stop();
      refresher.stop();
    };
  }, [appReady, uid, isLoggedIn, dispatch]);
};
